import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaRulerVertical, FaArrowLeft, FaInfoCircle } from 'react-icons/fa';
import SEO, { PageSEO, StructuredData } from '../../components/SEO';

const IdealBodyWeight = () => {
  const [unit, setUnit] = useState('metric');
  const [gender, setGender] = useState('male');
  const [height, setHeight] = useState('');
  const [heightFeet, setHeightFeet] = useState('');
  const [heightInches, setHeightInches] = useState('');
  const [frameSize, setFrameSize] = useState('medium');
  const [result, setResult] = useState(null);

  const calculateIdealWeight = (e) => {
    e.preventDefault();

    let heightInCm;

    if (unit === 'metric') {
      heightInCm = parseFloat(height);
    } else {
      const totalInches = (parseFloat(heightFeet) * 12) + parseFloat(heightInches || 0);
      heightInCm = totalInches * 2.54;
    }

    const heightInInches = heightInCm / 2.54;

    // Calculate using multiple formulas
    const formulas = {};

    // Devine Formula (1974)
    if (gender === 'male') {
      formulas.devine = 50 + 2.3 * (heightInInches - 60);
    } else {
      formulas.devine = 45.5 + 2.3 * (heightInInches - 60);
    }

    // Robinson Formula (1983)
    if (gender === 'male') {
      formulas.robinson = 52 + 1.9 * (heightInInches - 60);
    } else {
      formulas.robinson = 49 + 1.7 * (heightInInches - 60);
    }

    // Miller Formula (1983)
    if (gender === 'male') {
      formulas.miller = 56.2 + 1.41 * (heightInInches - 60);
    } else {
      formulas.miller = 53.1 + 1.36 * (heightInInches - 60);
    }

    // Hamwi Formula (1964)
    if (gender === 'male') {
      formulas.hamwi = 48 + 2.7 * (heightInInches - 60);
    } else {
      formulas.hamwi = 45.5 + 2.2 * (heightInInches - 60);
    }

    // BMI-based range (18.5-24.9)
    const heightInMeters = heightInCm / 100;
    formulas.bmiMin = 18.5 * heightInMeters * heightInMeters;
    formulas.bmiMax = 24.9 * heightInMeters * heightInMeters;

    // Frame size adjustment
    let frameAdjustment = 1;
    if (frameSize === 'small') frameAdjustment = 0.9;
    if (frameSize === 'large') frameAdjustment = 1.1;

    // Calculate average and range
    const idealWeights = [formulas.devine, formulas.robinson, formulas.miller, formulas.hamwi];
    const average = idealWeights.reduce((a, b) => a + b, 0) / idealWeights.length;
    const adjustedAverage = average * frameAdjustment;

    setResult({
      average: adjustedAverage.toFixed(1),
      range: {
        min: (formulas.bmiMin * frameAdjustment).toFixed(1),
        max: (formulas.bmiMax * frameAdjustment).toFixed(1),
      },
      formulas: {
        devine: (formulas.devine * frameAdjustment).toFixed(1),
        robinson: (formulas.robinson * frameAdjustment).toFixed(1),
        miller: (formulas.miller * frameAdjustment).toFixed(1),
        hamwi: (formulas.hamwi * frameAdjustment).toFixed(1),
      },
      frameSize,
    });
  };

  const resetCalculator = () => {
    setHeight('');
    setHeightFeet('');
    setHeightInches('');
    setResult(null);
  };

  const idealWeightStructuredData = StructuredData.calculator(
    'Ideal Body Weight Calculator',
    'Calculate your ideal body weight using multiple scientific formulas.',
    '/calculators/ideal-weight'
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO {...PageSEO.idealWeight} structuredData={idealWeightStructuredData} />
      {/* Header */}
      <section className="bg-gradient-to-br from-pink-500 to-rose-600 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/calculators" className="inline-flex items-center text-pink-100 hover:text-white mb-6 transition-colors">
            <FaArrowLeft className="mr-2" />
            All Calculators
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
              <FaRulerVertical className="text-2xl" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Ideal Body Weight</h1>
              <p className="text-pink-100 mt-1">Find your healthy weight range</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Enter Your Details</h2>

            {/* Unit Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
              <button
                type="button"
                onClick={() => setUnit('metric')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'metric' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Metric (cm)
              </button>
              <button
                type="button"
                onClick={() => setUnit('imperial')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  unit === 'imperial' ? 'bg-white shadow text-primary-600' : 'text-gray-600'
                }`}
              >
                Imperial (ft)
              </button>
            </div>

            <form onSubmit={calculateIdealWeight} className="space-y-5">
              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Gender</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setGender('male')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                      gender === 'male'
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => setGender('female')}
                    className={`py-3 px-4 rounded-xl border-2 font-medium transition-colors ${
                      gender === 'female'
                        ? 'border-pink-500 bg-pink-50 text-pink-700'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300'
                    }`}
                  >
                    Female
                  </button>
                </div>
              </div>

              {/* Height */}
              {unit === 'metric' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height (cm)</label>
                  <input
                    type="number"
                    value={height}
                    onChange={(e) => setHeight(e.target.value)}
                    placeholder="e.g., 170"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    required
                    min="140"
                    max="220"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Height</label>
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="number"
                      value={heightFeet}
                      onChange={(e) => setHeightFeet(e.target.value)}
                      placeholder="Feet"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      required
                      min="4"
                      max="7"
                    />
                    <input
                      type="number"
                      value={heightInches}
                      onChange={(e) => setHeightInches(e.target.value)}
                      placeholder="Inches"
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      min="0"
                      max="11"
                    />
                  </div>
                </div>
              )}

              {/* Frame Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Body Frame Size</label>
                <div className="grid grid-cols-3 gap-2">
                  {['small', 'medium', 'large'].map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setFrameSize(size)}
                      className={`py-3 px-2 rounded-xl border-2 font-medium text-sm capitalize transition-colors ${
                        frameSize === size
                          ? 'border-pink-500 bg-pink-50 text-pink-700'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Measure your wrist: Small (&lt;6"), Medium (6-7"), Large (&gt;7")
                </p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-pink-500 to-rose-600 text-white py-3 px-6 rounded-xl hover:from-pink-600 hover:to-rose-700 transition-colors font-semibold"
                >
                  Calculate
                </button>
                {result && (
                  <button
                    type="button"
                    onClick={resetCalculator}
                    className="px-6 py-3 border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium text-gray-700"
                  >
                    Reset
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Results */}
          <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Your Ideal Weight</h2>

            {result ? (
              <div className="space-y-6">
                {/* Main Result */}
                <div className="bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl p-6 text-white text-center">
                  <div className="text-pink-100 text-sm mb-1">Ideal Weight ({result.frameSize} frame)</div>
                  <div className="text-5xl font-bold mb-2">{result.average}</div>
                  <div className="text-pink-100">kg</div>
                </div>

                {/* Healthy Range */}
                <div className="bg-green-50 rounded-xl p-4">
                  <div className="text-green-700 font-medium mb-2">Healthy BMI Range</div>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-bold text-gray-900">{result.range.min}</span>
                    <span className="text-gray-400">to</span>
                    <span className="text-2xl font-bold text-gray-900">{result.range.max}</span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1">kg (BMI 18.5-24.9)</div>
                </div>

                {/* Formula Results */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">By Different Formulas</h3>
                  <div className="space-y-2">
                    {Object.entries(result.formulas).map(([formula, weight]) => (
                      <div key={formula} className="flex justify-between items-center bg-gray-50 rounded-lg px-4 py-2">
                        <span className="text-gray-600 capitalize">{formula}</span>
                        <span className="font-semibold text-gray-900">{weight} kg</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <Link
                  to="/pricing"
                  className="block w-full text-center bg-accent-500 text-white py-3 rounded-xl hover:bg-accent-600 transition-colors font-semibold"
                >
                  Get Your Personalized Plan
                </Link>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FaRulerVertical className="text-5xl mx-auto mb-4 text-gray-300" />
                <p>Enter your height and gender to find your ideal weight</p>
              </div>
            )}
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-sm p-6 md:p-8 border border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <FaInfoCircle className="text-pink-600 text-xl" />
            <h3 className="text-lg font-bold text-gray-900">About Ideal Body Weight</h3>
          </div>
          <div className="prose prose-gray max-w-none">
            <p className="text-gray-600 mb-4">
              This calculator uses four well-known formulas to estimate ideal body weight, along with adjustments for body frame size.
              The results provide a general guideline, not an absolute target.
            </p>
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Formulas Used</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li><strong>Devine (1974):</strong> Originally for drug dosing</li>
                  <li><strong>Robinson (1983):</strong> Modified Devine formula</li>
                  <li><strong>Miller (1983):</strong> Tends to give lower values</li>
                  <li><strong>Hamwi (1964):</strong> Commonly used in clinical settings</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Determining Frame Size</h4>
                <p className="text-sm text-gray-600">
                  Wrap your thumb and middle finger around your wrist. If they overlap, you have a small frame.
                  If they touch, medium. If they don't touch, large frame.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IdealBodyWeight;
